import isEmpty from './isEmpty'
import loggerUtil from './loggerUtil'
import nodeMime from 'mime'

export default class LocalFileUtil {
  /**
   * Gets the content of a File object
   * @param {File|Blob} file The File or Blob object to retrieve content from
   * @returns {Promise<String>} The file as a base64 data string
   */
  static async getFileContent (file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = event => {
        // e.target.result is a base64 string
        if (event.target && !isEmpty(event.target.result)) {
          resolve(event.target.result)
        } else {
          reject(new Error('File cannot be read'))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  /**
   * Gets the mime-type for a given file extension.
   * @param {String} fileUri - The full file URI, the file name with extension, or just the file extension to get the mime type of.
   * @returns {String|Boolean} The mime type matching the file extension or false if mime type is not found
   */
  static getMimeType (fileUri) {
    const extension = fileUri.includes('.') ? fileUri.substring(fileUri.lastIndexOf('.') + 1) : fileUri
    const mime = nodeMime.getType(extension)

    if (typeof mime === 'boolean') {
      loggerUtil.error(`Error getting mime type for ${extension}`)
      throw new Error('Unknown mime type')
    }

    return mime
  }

  /**
   * Gets a Blob object from a file's Base64 content.
   * @param {String} b64Data - The file's Base64 content.
   * @param {String} [contentType] - The file's mime type.
   * @param {Number} [sliceSize] - How many bytes to process at a time.
   * @returns {Promise<Blob>} The file's Blob objects.
   * @remarks - From https://newbedev.com/creating-a-blob-from-a-base64-string-in-javascript
   */
  static b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = window.atob(b64Data) // workaround mentioned here: https://github.com/microsoft/TypeScript/issues/45566
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return blob
  }
}
